// The map's outline, as a function of bearing.
//
// Every map object is a disc — `(x, y, layout.radius * zoom)` — and the layout
// solver puts them all in absolute polar coordinates around a single origin
// pinned at world (0,0). So "how far out does the map reach at bearing θ" is a
// well-defined question with a cheap answer, and it is the only thing the
// callout leader lines need in order to route around the map rather than
// through it.
//
// The array is filled from the object loop `animateAndLineCartesian` already
// runs every frame — that loop has each object's resolved radius and zoom in
// hand for `mainBounds`, so the marginal cost here is a few array writes per
// object.
//
// The stored value never under-estimates. A point further out than the outline
// at its bearing is provably outside every disc; a point inside may or may not
// be. Routing only needs the first half of that, and over-estimating is the
// safe direction to be wrong in — so the per-bucket value is evaluated at the
// point of the bucket *closest* to the disc's centre, not at the bucket centre.

import { camera } from "../camera";

export const BUCKETS = 256; // power of two: index with & (BUCKETS - 1)

const BUCKET_ANGLE = (Math.PI * 2) / BUCKETS;
const HALF_BUCKET = BUCKET_ANGLE * 0.5;
const COS_HALF = Math.cos(HALF_BUCKET);
const SIN_HALF = Math.sin(HALF_BUCKET);
const TWO_PI = Math.PI * 2;

// World units per millisecond. The outline snaps outwards the instant something
// grows into it, but relaxes inwards slowly, so a scope winking out of existence
// doesn't yank every leader routed over it.
const FALL_RATE = 0.5;

const radii = new Float32Array(BUCKETS);
const previous = new Float32Array(BUCKETS);

// Bearing of each bucket's centre, precomputed so the accumulation loop needs no
// trigonometry per bucket.
const bucketCos = new Float32Array(BUCKETS);
const bucketSin = new Float32Array(BUCKETS);

for (let i = 0; i < BUCKETS; i++) {
  const theta = (i + 0.5) * BUCKET_ANGLE - Math.PI;
  bucketCos[i] = Math.cos(theta);
  bucketSin[i] = Math.sin(theta);
}

// Objects covering the origin subtend every bearing at once. In practice that is
// only ever the root dial, so it is tracked as a scalar rather than smeared
// across the whole array.
let originFloor = 0;
let populated = false;

export function begin() {
  radii.fill(0);
  originFloor = 0;
}

// `x`/`y` are the object's world centre, `r` its drawn world radius.
export function addDisc(x, y, r) {
  if (!(r > 0)) return;

  const d = Math.sqrt(x * x + y * y);

  if (d <= r) {
    const reach = d + r;
    if (reach > originFloor) originFloor = reach;
    return;
  }

  const delta = Math.asin(r / d);
  const beta = Math.atan2(y, x);
  const cb = x / d;
  const sb = y / d;

  // Where the disc's own outline meets its silhouette edge — its reach at any
  // bearing beyond the wedge, and the floor for buckets that only clip it.
  const tangent = Math.sqrt(d * d - r * r);

  const first = Math.floor((beta - delta + Math.PI) / BUCKET_ANGLE);
  const last = Math.ceil((beta + delta + Math.PI) / BUCKET_ANGLE);

  const r2 = r * r;
  const d2 = d * d;

  for (let k = first; k <= last; k++) {
    const i = k & (BUCKETS - 1);

    // Offset of this bucket's centre from the disc's bearing, via the dot and
    // cross products against the precomputed bucket direction — no trig.
    const cos = bucketCos[i] * cb + bucketSin[i] * sb;
    const sin = bucketSin[i] * cb - bucketCos[i] * sb;

    // The nearest approach to the disc's bearing anywhere inside this bucket,
    // i.e. |offset| - half a bucket, floored at zero. Evaluating there rather
    // than at the bucket centre is what keeps the value an over-estimate for
    // buckets the disc only partly covers.
    let sinPhi = Math.abs(sin) * COS_HALF - cos * SIN_HALF;
    if (sinPhi < 0) sinPhi = 0;

    const inside = r2 - d2 * sinPhi * sinPhi;
    const reach =
      inside > 0
        ? d * Math.sqrt(1 - sinPhi * sinPhi) + Math.sqrt(inside)
        : tangent;

    if (reach > radii[i]) radii[i] = reach;
  }
}

export function end(deltaMs) {
  const fall = FALL_RATE * (deltaMs > 0 ? deltaMs : 0);

  for (let i = 0; i < BUCKETS; i++) {
    let v = radii[i];
    if (v < originFloor) v = originFloor;

    const relaxed = previous[i] - fall;
    if (populated && v < relaxed) v = relaxed;

    radii[i] = v;
    previous[i] = v;
  }

  populated = true;
}

export function bucketOf(theta) {
  const t = (((theta + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI;
  return Math.floor(t / BUCKET_ANGLE) & (BUCKETS - 1);
}

// World reach at a single bearing. Fine for inspection; routing should use
// `worldRangeMax` so a segment is cleared along its whole length rather than at
// the two points that happen to be sampled.
export function worldRadiusAt(theta) {
  return populated ? radii[bucketOf(theta)] : 0;
}

// The largest reach over every bucket between two bearings, taking the short way
// round. This is what makes a polyline segment safe: a narrow gap between two
// neighbouring objects can no longer pull a leader down into it and back out.
export function worldRangeMax(from, to) {
  if (!populated) return 0;

  let a = bucketOf(from);
  const b = bucketOf(to);

  let span = b - a;
  if (span < 0) span += BUCKETS;
  if (span > BUCKETS >> 1) {
    a = b;
    span = BUCKETS - span;
  }

  let m = 0;
  for (let i = 0; i <= span; i++) {
    const v = radii[(a + i) & (BUCKETS - 1)];
    if (v > m) m = v;
  }
  return m;
}

export function viewRangeMax(from, to) {
  return worldRangeMax(from, to) * camera.scale;
}

// The map's origin in view space — the pole every bearing is measured about.
// `camera.x`/`y` track the centre of the map's *bounding box*, not the origin,
// so this is generally offset from the middle of the viewport.
export function centreX() {
  return -camera.x * camera.scale;
}

export function centreY() {
  return -camera.y * camera.scale;
}

// Distance from the map centre to the viewport edge along a bearing. This is the
// honest measure of how much room a leader has, and it has to be measured rather
// than assumed: `autoSize` only corrects the scale once it drifts outside a ±10%
// deadband, so the map can be up to 10% larger than its nominal fit and the
// clearance over the poles can be negative.
export function viewportRadiusAt(theta, bounds) {
  const cx = centreX();
  const cy = centreY();
  const dx = Math.cos(theta);
  const dy = Math.sin(theta);

  let t = Infinity;

  if (dx > 1e-6) t = Math.min(t, (bounds.x2 - cx) / dx);
  else if (dx < -1e-6) t = Math.min(t, (bounds.x1 - cx) / dx);

  if (dy > 1e-6) t = Math.min(t, (bounds.y2 - cy) / dy);
  else if (dy < -1e-6) t = Math.min(t, (bounds.y1 - cy) / dy);

  return t;
}

export function isPopulated() {
  return populated;
}

// For the debug overlay; callers must not write to it.
export function rawBuckets() {
  return radii;
}
